<script setup lang="ts">
import { useActivityProjectStore } from '@/store/activityProjectStore/activityProjectStore';
import { useChecklistStore } from '@/store/checklistStore';
import { useMilestoneProjectStore } from '@/store/milestoneProjectStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectStore } from '@/store/projectStore/projectStore';
import { useMapStore } from '@/store/remapStore/remapStore';
import { useSocketStore } from '@/store/socketStore';
import { getTenant } from '@/utils/helpers/urlManipulation';

interface ProjectUpdatedPayload {
  projectId: string;
  userId: string;
  operationType?: string;
  entityType?: string;
  entityId?: string;
}

const socketStore = useSocketStore();
const profileStore = useProfileStore();
const notificationsStore = useNotificationStore();
const projectStore = useProjectStore();
const activityStore = useActivityProjectStore();
const checklistStore = useChecklistStore();
const milestoneStore = useMilestoneProjectStore();
const mapStore = useMapStore();
const route = useRoute();

const isBeeboardTenant = computed(() => getTenant() === 'beeboard');

/**
 * Project sub-routes that render live project data and must therefore react to
 * other users' changes. The map, list and Kanban views all read the same
 * socket-fed stores, so each one needs incoming `project:updated` events
 * applied; other sub-pages (overview, history, settings, …) do not.
 */
const LIVE_UPDATE_ROUTES = new Set([
  'ProjectPage',
  'ProjectListView',
  'ProjectKanbanView',
]);

/**
 * Operations where we can safely fetch and patch a single entity
 * instead of reloading the entire project.
 */
const PATCHABLE_OPERATIONS: Record<string, 'activity' | 'milestone'> = {
  'history.activity.updateActivity': 'activity',
  'history.activity.toggleFavorite': 'activity',
  'history.milestone.updateMilestone': 'milestone',
};

/**
 * Checklist operations (WI #5577). Checklist data is only rendered inside
 * the checklist section itself (not on the map/list/kanban views), so these
 * events bypass the LIVE_UPDATE_ROUTES gate and simply refetch the checklist
 * store — but only when the affected grid/pool is currently loaded.
 *
 * Note: undoing a checklist operation does NOT arrive here — the backend
 * undo endpoint runs on a plain transactionContext and emits no
 * `project:updated` event. The current user's own undo is covered by the
 * post-undo refetch in historyStore.undoOperation instead; other users'
 * sessions are not refreshed until their next (re)load.
 */
const CHECKLIST_ACTIVITY_OPERATIONS = new Set([
  'history.activity.addChecklistStep',
  'history.activity.updateChecklistStep',
  'history.activity.removeChecklistStep',
  'history.activity.updateChecklistRowSource',
  'history.activity.addChecklistRow',
  'history.activity.updateChecklistRow',
  'history.activity.removeChecklistRow',
  'history.activity.updateChecklistCell',
]);
const CHECKLIST_POOL_OPERATIONS = new Set([
  'history.project.addProjectChecklistRow',
  'history.project.updateProjectChecklistRow',
  'history.project.removeProjectChecklistRow',
]);

/**
 * Debounce timer and stale-response guard for full project reloads.
 * Prevents concurrent out-of-order responses and coalesces rapid-fire events.
 */
const RELOAD_DEBOUNCE_MS = 200;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
let reloadGeneration = 0;

/**
 * Checklist events carry a sub-entity id (step/row/cell), not the activity id,
 * so a single change fans out to a refetch of every loaded grid in the project.
 * A burst from another user (bulk row add, rapid toggles) would otherwise issue
 * that fan-out per event. Coalesce them: collect which refresh kinds are needed
 * per project and run one trailing pass after the debounce window.
 */
interface PendingChecklistRefresh {
  activity: boolean;
  pool: boolean;
  timer: ReturnType<typeof setTimeout>;
}
const checklistRefreshes = new Map<string, PendingChecklistRefresh>();

function scheduleChecklistRefresh(
  projectId: string,
  kind: 'activity' | 'pool',
): void {
  const pending = checklistRefreshes.get(projectId);
  if (pending) {
    pending[kind] = true;

    return;
  }
  const entry: PendingChecklistRefresh = {
    activity: kind === 'activity',
    pool: kind === 'pool',
    timer: setTimeout(() => {
      checklistRefreshes.delete(projectId);
      if (entry.activity) {
        // Store action no-ops unless a grid of this project is loaded
        void checklistStore.refreshActivityChecklist(projectId).catch(() => {
          // Grid may reference a deleted activity — nothing to refresh then
        });
      }
      if (entry.pool) {
        void checklistStore.refreshPoolRows(projectId).catch(() => {
          // Pool may be gone — nothing to refresh then
        });
      }
    }, RELOAD_DEBOUNCE_MS),
  };
  checklistRefreshes.set(projectId, entry);
}

// Leaving a project drops its cached checklist grids/pool so they don't grow
// unbounded or keep getting refreshed by socket events for a project the user
// has navigated away from. Fires on an actual project switch (A -> B or
// A -> non-project route), not on navigation within the same project.
watch(
  () => route.params.projectId as string | undefined,
  (newId, oldId) => {
    if (oldId && oldId !== newId) {
      checklistStore.clearProject(oldId);
    }
  },
);

onMounted(() => {
  try {
    socketStore.createSocketConnection();

    socketStore.listenEvent(
      'project:updated',
      handleProjectUpdatedEvent as unknown as (
        payload: Record<string, string | number>,
      ) => void,
    );

    // Catch up after socket reconnection — events during disconnect are lost
    socketStore.socket.io.on('reconnect', handleReconnect);
  } catch (err) {
    console.log('WebSocket connection failed', err);
  }
});

onBeforeUnmount(() => {
  if (reloadTimer) {
    clearTimeout(reloadTimer);
  }
  checklistRefreshes.forEach((entry) => clearTimeout(entry.timer));
  checklistRefreshes.clear();
  socketStore.destroySocketConnection();
});

function handleReconnect() {
  if (!LIVE_UPDATE_ROUTES.has(route.name as string)) {
    return;
  }
  const projectId = route.params.projectId as string;
  scheduleFullReload(projectId);
}

async function handleProjectUpdatedEvent(payload: ProjectUpdatedPayload) {
  const userId = profileStore.profile.id;
  if (userId === payload.userId) {
    return;
  }

  // Checklist events are handled regardless of the current route — the
  // store actions no-op unless the affected grid/pool is loaded.
  if (
    payload.operationType &&
    CHECKLIST_ACTIVITY_OPERATIONS.has(payload.operationType)
  ) {
    scheduleChecklistRefresh(payload.projectId, 'activity');

    return;
  }
  if (
    payload.operationType &&
    CHECKLIST_POOL_OPERATIONS.has(payload.operationType)
  ) {
    // refreshPoolRows already refreshes the pool and every loaded pool-mode
    // grid (which mirror it); individual grids are unaffected.
    scheduleChecklistRefresh(payload.projectId, 'pool');

    return;
  }

  if (!LIVE_UPDATE_ROUTES.has(route.name as string)) {
    return;
  }

  // Skip processing when tab is hidden — a single reload on tab focus is enough
  if (document.visibilityState === 'hidden') {
    scheduleFullReload(payload.projectId);

    return;
  }

  const projectId = route.params.projectId as string;
  const { operationType, entityId, entityType } = payload;
  const patchTarget = operationType
    ? PATCHABLE_OPERATIONS[operationType]
    : undefined;

  if (patchTarget && entityId) {
    try {
      if (patchTarget === 'activity') {
        await activityStore.loadActivity(projectId, entityId);
      } else if (patchTarget === 'milestone') {
        await milestoneStore.loadMilestone(projectId, entityId);
      }

      return;
    } catch {
      // If targeted fetch fails, fall back to full reload
    }
  }

  // Full project reload for all other operations (debounced)
  scheduleFullReload(projectId, entityType, entityId);
}

function scheduleFullReload(
  projectId: string,
  entityType?: string,
  entityId?: string,
) {
  if (reloadTimer) {
    clearTimeout(reloadTimer);
  }
  reloadTimer = setTimeout(
    () => void executeFullReload(projectId, entityType, entityId),
    RELOAD_DEBOUNCE_MS,
  );
}

async function executeFullReload(
  projectId: string,
  entityType?: string,
  entityId?: string,
) {
  const generation = ++reloadGeneration;

  // Refresh entity details BEFORE the project summary so that
  // when loadProject triggers a re-render, detail data is already fresh.
  // This is best-effort — it may 404 on deletes, which is fine.
  try {
    if (entityId && entityType === 'Activity') {
      if (activityStore.hasActivity(projectId, entityId)) {
        await activityStore.loadActivity(projectId, entityId);
      }
    } else if (entityId && entityType === 'Milestone') {
      if (milestoneStore.hasMilestone(projectId, entityId)) {
        await milestoneStore.loadMilestone(projectId, entityId);
      }
    }
  } catch {
    // Entity may have been deleted — proceed with project reload
  }

  // Stale response guard: if a newer reload was scheduled, skip this one
  if (generation !== reloadGeneration) {
    return;
  }

  try {
    await projectStore.loadProject(projectId);

    // Another reload may have been scheduled while we were awaiting
    if (generation !== reloadGeneration) {
      return;
    }

    await clearStaleSelection(projectId);
  } catch {
    // If auto-reload fails, show the manual refresh button as fallback
    notificationsStore.isProjectRefreshRequired = true;
  }
}

async function clearStaleSelection(projectId: string) {
  const { selectedId, selectedContext } = mapStore;
  if (!selectedId || !selectedContext) {
    return;
  }

  let stillExists = true;

  switch (selectedContext) {
    case 'activity':
    case 'mirroredActivity':
      stillExists = projectStore.hasActivitySummary(projectId, selectedId);
      break;
    case 'milestone':
    case 'mirroredMilestone':
      stillExists = projectStore.hasMilestoneSummary(projectId, selectedId);
      break;
    case 'phase':
      stillExists = projectStore
        .project(projectId)
        .phases.some(({ id }) => id === selectedId);
      break;
    case 'workstream':
      stillExists = projectStore
        .project(projectId)
        .workstreams.some(({ id }) => id === selectedId);
      break;
  }

  if (!stillExists) {
    await mapStore.mapSelectItem();
  }
}
</script>
<template>
  <div
    v-if="isBeeboardTenant"
    class="connection-indicator"
    :data-connected="socketStore.isConnected"
  />
</template>

<style lang="scss" scoped>
.connection-indicator {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 10px;
  height: 10px;
  border-radius: var(--border-radius-circle);
  margin: var(--space-10);
  background-color: red;
  z-index: 1000;
  transition: background-color var(--duration-slow) var(--ease-standard);

  &[data-connected='true'] {
    background-color: green;
  }
}
</style>
